import { Button, IllustratedMessage, Pagination, Progress, Stack } from '@eleveo/components-react';
import { ImportIcon, RefreshIcon, SearchNoneIcon } from '@eleveo/icons-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../app/AuthorizationProvider';
import { useConfig } from '../../app/ConfigurationProvider';
import { Header } from '../../components/LayoutNext/Header';
import { Content, Panel, SidebarWrapper } from '../../components/LayoutNext/LayoutNext';
import { Center } from '../../components/LayoutNext/components/Center';
import { useSidebar } from '../../components/layout/Sidebar';
import { useGetDataImportHistoryQuery } from '../../graphql/components';
import { DataImportSidebar } from './DataImportSidebar';
import { DataImportTable } from './DataImportTable';
import { DataImporter } from './DataImporter';
import { CONTAINER_TAG, IMPORT_DATA_BUTTON_TAG, TABLE_REFRESH_BUTTON_TAG, TITLE_TAG } from './constants';

export const DataImportScene: React.FC = () => {
  const { locale, keycloak, forceUpdateToken, isTokenInvalid } = useAuth();
  const config = useConfig();
  const { t } = useTranslation();

  const { isSidebarOpen, closeSidebar, toggleSidebar } = useSidebar();

  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 25,
  });

  const { data, refetch, loading, error } = useGetDataImportHistoryQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      limit: pagination.pageSize,
      offset: pagination.pageSize * (pagination.page - 1),
    },
  });

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination((state) => ({
      ...state,
      page,
      pageSize: pageSize ?? state.pageSize,
    }));
  };

  const handleReset = () => {
    handlePageChange(1);
    refetch();
  };

  const historyRecords = data?.dataImportHistory?.dataImportHistoryRecords ?? [];

  return (
    <DataImporter
      t={t}
      locale={locale}
      keycloak={keycloak}
      forceUpdateToken={forceUpdateToken}
      isTokenInvalid={isTokenInvalid}
      config={config}
      onUploadFinish={() => {
        closeSidebar();
        handleReset();
      }}
    >
      {(passedProps) => (
        <>
          <Header title={t('dataImport.dataImport')} testId={TITLE_TAG} />

          <Panel>
            <Stack direction="horizontal" className="w-full" spacing={15}>
              <Button isSelected={isSidebarOpen} testId={IMPORT_DATA_BUTTON_TAG} onClick={toggleSidebar}>
                <ImportIcon />
                {t('dataImport.importDataLabel')}
              </Button>

              <Button
                aria-label={t('filter.refresh')}
                className="ml-auto"
                onClick={handleReset}
                testId={TABLE_REFRESH_BUTTON_TAG}
              >
                <RefreshIcon />
              </Button>
            </Stack>
          </Panel>

          <Content testId={CONTAINER_TAG}>
            {passedProps.uploadPending && <Progress withOverlay value={passedProps.uploadProgress} />}

            {loading && <Progress withOverlay />}

            {!loading && !error && historyRecords.length === 0 && (
              <Center>
                <IllustratedMessage heading={t('dataImport.noEntries')} icon={<SearchNoneIcon />} />
              </Center>
            )}

            {!loading && !error && historyRecords.length > 0 && (
              <DataImportTable
                data={historyRecords}
                pagination={
                  <Pagination
                    onPageChange={handlePageChange}
                    selectedPage={pagination.page}
                    selectedPageSize={pagination.pageSize}
                    totalItems={data?.dataImportHistory.totalCount ?? 0}
                  />
                }
              />
            )}
          </Content>

          <SidebarWrapper>
            {isSidebarOpen && <DataImportSidebar passedProps={passedProps} handleCloseSidebar={closeSidebar} />}
          </SidebarWrapper>
        </>
      )}
    </DataImporter>
  );
};
